import { cn } from '@tempo-io/sui-class-utils';
import {
  MIN_HEIGHT_PX,
  MIN_PRIMARY_FRACTION,
  type useDashboardGridSizing,
} from './useDashboardGridSizing';
import { useGridResizeHandle } from './useGridResizeHandle';

type SizingApi = ReturnType<typeof useDashboardGridSizing>;

const HANDLE_HITBOX = 12;
const GAP = 16; // matches `gap-4` (1rem)

export const DashboardGridResizeHandles = ({
  gridEl,
  sizingApi,
  scrollContainer,
}: {
  gridEl: HTMLDivElement | null;
  sizingApi: SizingApi;
  scrollContainer: HTMLElement | null;
}) => {
  const { descriptor } = sizingApi;
  const isRow = descriptor.orientation === 'row';

  return (
    <>
      {descriptor.stripes.slice(0, -1).map((stripe, stripeIdx) => {
        const stripeKey = stripe.cells[0]?.position ?? stripeIdx;
        return isRow ? (
          <BetweenRowStripesHandle
            key={`between-stripe-${stripeKey}`}
            stripeIdx={stripeIdx}
            sizingApi={sizingApi}
          />
        ) : (
          <BetweenColumnStripesHandle
            key={`between-stripe-${stripeKey}`}
            stripeIdx={stripeIdx}
            gridEl={gridEl}
            sizingApi={sizingApi}
          />
        );
      })}
      {descriptor.stripes.flatMap((stripe, stripeIdx) =>
        stripe.cells
          .slice(0, -1)
          .map((cell) =>
            isRow ? (
              <WithinRowStripeHandle
                key={`within-${cell.position}`}
                stripeIdx={stripeIdx}
                cellIdx={stripe.cells.indexOf(cell)}
                gridEl={gridEl}
                sizingApi={sizingApi}
              />
            ) : (
              <WithinColumnStripeHandle
                key={`within-${cell.position}`}
                stripeIdx={stripeIdx}
                cellIdx={stripe.cells.indexOf(cell)}
                sizingApi={sizingApi}
              />
            ),
          ),
      )}
      <BottomEdgeHandle sizingApi={sizingApi} scrollContainer={scrollContainer} />
    </>
  );
};

const sumPrimaryUpTo = (sizingApi: SizingApi, stripeIdx: number): number => {
  let total = 0;
  for (let i = 0; i < stripeIdx; i++) {
    total += sizingApi.sizing.stripes[i]?.primarySize ?? 0;
  }
  return total;
};

const stripeHeightTotal = (sizingApi: SizingApi, stripeIdx: number): number => {
  return (sizingApi.sizing.stripes[stripeIdx]?.cellCrossSizes ?? []).reduce((a, b) => a + b, 0);
};

const dashboardTotalHeight = (sizingApi: SizingApi): number => {
  if (sizingApi.descriptor.orientation === 'row') {
    const stripes = sizingApi.sizing.stripes;
    return stripes.reduce((a, s) => a + s.primarySize, 0) + GAP * Math.max(0, stripes.length - 1);
  }
  let max = 0;
  for (let i = 0; i < sizingApi.sizing.stripes.length; i++) {
    const h =
      stripeHeightTotal(sizingApi, i) +
      GAP * Math.max(0, (sizingApi.sizing.stripes[i]?.cellCrossSizes.length ?? 0) - 1);
    if (h > max) max = h;
  }
  return max;
};

const BetweenRowStripesHandle = ({
  stripeIdx,
  sizingApi,
}: {
  stripeIdx: number;
  sizingApi: SizingApi;
}) => {
  const cumBefore = sumPrimaryUpTo(sizingApi, stripeIdx + 1);
  const topPx = cumBefore + stripeIdx * GAP + GAP / 2 - HANDLE_HITBOX / 2;

  const { isDragging, onPointerDown } = useGridResizeHandle<{
    top: number;
    bottom: number;
    total: number;
  }>({
    axis: 'y',
    getStart: () => {
      const a = sizingApi.sizing.stripes[stripeIdx]?.primarySize ?? 0;
      const b = sizingApi.sizing.stripes[stripeIdx + 1]?.primarySize ?? 0;
      return { top: a, bottom: b, total: a + b };
    },
    onMove: (start, deltaPx) => {
      const nextTop = clampPair(start.top + deltaPx, start.total);
      sizingApi.previewStripePair(stripeIdx, nextTop, start.total - nextTop);
    },
    onCommit: (start, deltaPx) => {
      const nextTop = clampPair(start.top + deltaPx, start.total);
      sizingApi.commitStripePair(stripeIdx, nextTop, start.total - nextTop);
    },
  });

  return (
    <div
      data-dragging={isDragging}
      onPointerDown={onPointerDown}
      className="absolute left-0 right-0 z-20 cursor-row-resize flex items-center justify-stretch"
      style={{ top: `${topPx}px`, height: `${HANDLE_HITBOX}px` }}
    >
      <div
        className={cn(
          'h-px w-full transition-colors bg-transparent data-[dragging=true]:bg-bg-info',
        )}
        data-dragging={isDragging}
      />
    </div>
  );
};

const BetweenColumnStripesHandle = ({
  stripeIdx,
  gridEl,
  sizingApi,
}: {
  stripeIdx: number;
  gridEl: HTMLDivElement | null;
  sizingApi: SizingApi;
}) => {
  const cumFraction = sizingApi.sizing.stripes
    .slice(0, stripeIdx + 1)
    .reduce((a, s) => a + s.primarySize, 0);
  const stripeCount = sizingApi.descriptor.stripes.length;
  const trackWidthFactor = `(100% - ${(stripeCount - 1) * GAP}px)`;
  const leftCss = `calc(${cumFraction} * ${trackWidthFactor} + ${stripeIdx * GAP + GAP / 2 - HANDLE_HITBOX / 2}px)`;

  const { isDragging, onPointerDown } = useGridResizeHandle<{
    pairTotalFraction: number;
    leftFractionAtStart: number;
    tracksWidth: number;
  }>({
    axis: 'x',
    getStart: () => {
      const left = sizingApi.sizing.stripes[stripeIdx]?.primarySize ?? 0;
      const right = sizingApi.sizing.stripes[stripeIdx + 1]?.primarySize ?? 0;
      const pair = left + right;
      return {
        pairTotalFraction: pair,
        leftFractionAtStart: pair > 0 ? left / pair : 0.5,
        tracksWidth: Math.max(
          1,
          (gridEl?.getBoundingClientRect().width ?? 1) - (stripeCount - 1) * GAP,
        ),
      };
    },
    onMove: (start, deltaPx) => {
      const pairTrackWidth = start.pairTotalFraction * start.tracksWidth;
      const nextLeftFraction =
        start.leftFractionAtStart + (pairTrackWidth > 0 ? deltaPx / pairTrackWidth : 0);
      const left = clampStripePair(
        nextLeftFraction * start.pairTotalFraction,
        start.pairTotalFraction,
      );
      sizingApi.previewStripePair(stripeIdx, left, start.pairTotalFraction - left);
    },
    onCommit: (start, deltaPx) => {
      const pairTrackWidth = start.pairTotalFraction * start.tracksWidth;
      const nextLeftFraction =
        start.leftFractionAtStart + (pairTrackWidth > 0 ? deltaPx / pairTrackWidth : 0);
      const left = clampStripePair(
        nextLeftFraction * start.pairTotalFraction,
        start.pairTotalFraction,
      );
      sizingApi.commitStripePair(stripeIdx, left, start.pairTotalFraction - left);
    },
  });

  return (
    <div
      data-dragging={isDragging}
      onPointerDown={onPointerDown}
      className="absolute top-0 bottom-0 z-20 cursor-col-resize flex items-stretch justify-center"
      style={{ left: leftCss, width: `${HANDLE_HITBOX}px` }}
    >
      <div
        className={cn(
          'w-px h-full transition-colors bg-transparent data-[dragging=true]:bg-bg-info',
        )}
        data-dragging={isDragging}
      />
    </div>
  );
};

const WithinRowStripeHandle = ({
  stripeIdx,
  cellIdx,
  gridEl,
  sizingApi,
}: {
  stripeIdx: number;
  cellIdx: number;
  gridEl: HTMLDivElement | null;
  sizingApi: SizingApi;
}) => {
  const stripe = sizingApi.sizing.stripes[stripeIdx];
  const cellCount = stripe?.cellCrossSizes.length ?? 0;
  const cumPrimary = sumPrimaryUpTo(sizingApi, stripeIdx);
  const topPx = cumPrimary + stripeIdx * GAP;
  const stripeHeight = stripe?.primarySize ?? 0;
  const cumFraction = stripe?.cellCrossSizes.slice(0, cellIdx + 1).reduce((a, b) => a + b, 0) ?? 0;
  const trackWidthFactor = `(100% - ${Math.max(0, cellCount - 1) * GAP}px)`;
  const leftCss = `calc(${cumFraction} * ${trackWidthFactor} + ${cellIdx * GAP + GAP / 2 - HANDLE_HITBOX / 2}px)`;

  const { isDragging, onPointerDown } = useGridResizeHandle<{
    pairTotalFraction: number;
    leftFractionAtStart: number;
    tracksWidth: number;
  }>({
    axis: 'x',
    getStart: () => {
      const left = stripe?.cellCrossSizes[cellIdx] ?? 0;
      const right = stripe?.cellCrossSizes[cellIdx + 1] ?? 0;
      const pair = left + right;
      return {
        pairTotalFraction: pair,
        leftFractionAtStart: pair > 0 ? left / pair : 0.5,
        tracksWidth: Math.max(
          1,
          (gridEl?.getBoundingClientRect().width ?? 1) - Math.max(0, cellCount - 1) * GAP,
        ),
      };
    },
    onMove: (start, deltaPx) => {
      const pairTrackWidth = start.pairTotalFraction * start.tracksWidth;
      const next = start.leftFractionAtStart + (pairTrackWidth > 0 ? deltaPx / pairTrackWidth : 0);
      sizingApi.previewCrossPair(stripeIdx, cellIdx, next);
    },
    onCommit: (start, deltaPx) => {
      const pairTrackWidth = start.pairTotalFraction * start.tracksWidth;
      const next = start.leftFractionAtStart + (pairTrackWidth > 0 ? deltaPx / pairTrackWidth : 0);
      sizingApi.commitCrossPair(stripeIdx, cellIdx, next);
    },
  });

  if (!stripe) return null;

  return (
    <div
      data-dragging={isDragging}
      onPointerDown={onPointerDown}
      className="absolute z-20 cursor-col-resize flex items-stretch justify-center"
      style={{
        top: `${topPx}px`,
        height: `${stripeHeight}px`,
        left: leftCss,
        width: `${HANDLE_HITBOX}px`,
      }}
    >
      <div
        className={cn(
          'w-px h-full transition-colors bg-transparent data-[dragging=true]:bg-bg-info',
        )}
        data-dragging={isDragging}
      />
    </div>
  );
};

const WithinColumnStripeHandle = ({
  stripeIdx,
  cellIdx,
  sizingApi,
}: {
  stripeIdx: number;
  cellIdx: number;
  sizingApi: SizingApi;
}) => {
  const stripe = sizingApi.sizing.stripes[stripeIdx];
  const stripeCount = sizingApi.descriptor.stripes.length;
  const cumFractionBefore = sizingApi.sizing.stripes
    .slice(0, stripeIdx)
    .reduce((a, s) => a + s.primarySize, 0);
  const trackWidthFactor = `(100% - ${(stripeCount - 1) * GAP}px)`;
  const leftCss = `calc(${cumFractionBefore} * ${trackWidthFactor} + ${stripeIdx * GAP}px)`;
  const stripeWidthCss = `calc(${stripe?.primarySize ?? 0} * ${trackWidthFactor})`;
  const cumHeight = stripe?.cellCrossSizes.slice(0, cellIdx + 1).reduce((a, b) => a + b, 0) ?? 0;
  const topPx = cumHeight + cellIdx * GAP + GAP / 2 - HANDLE_HITBOX / 2;

  const { isDragging, onPointerDown } = useGridResizeHandle<{
    top: number;
    bottom: number;
    total: number;
  }>({
    axis: 'y',
    getStart: () => {
      const a = stripe?.cellCrossSizes[cellIdx] ?? 0;
      const b = stripe?.cellCrossSizes[cellIdx + 1] ?? 0;
      return { top: a, bottom: b, total: a + b };
    },
    onMove: (start, deltaPx) => {
      const nextTop = clampPair(start.top + deltaPx, start.total);
      sizingApi.previewCrossPairPx(stripeIdx, cellIdx, nextTop, start.total - nextTop);
    },
    onCommit: (start, deltaPx) => {
      const nextTop = clampPair(start.top + deltaPx, start.total);
      sizingApi.commitCrossPairPx(stripeIdx, cellIdx, nextTop, start.total - nextTop);
    },
  });

  if (!stripe) return null;

  return (
    <div
      data-dragging={isDragging}
      onPointerDown={onPointerDown}
      className="absolute z-20 cursor-row-resize flex items-center justify-stretch"
      style={{
        top: `${topPx}px`,
        height: `${HANDLE_HITBOX}px`,
        left: leftCss,
        width: stripeWidthCss,
      }}
    >
      <div
        className={cn(
          'h-px w-full transition-colors bg-transparent data-[dragging=true]:bg-bg-info',
        )}
        data-dragging={isDragging}
      />
    </div>
  );
};

const BottomEdgeHandle = ({
  sizingApi,
  scrollContainer,
}: {
  sizingApi: SizingApi;
  scrollContainer: HTMLElement | null;
}) => {
  const totalHeight = dashboardTotalHeight(sizingApi);

  const { isDragging, onPointerDown } = useGridResizeHandle<{ height: number }>({
    axis: 'y',
    getStart: () => ({ height: totalHeight }),
    onMove: (start, deltaPx) => sizingApi.previewBottomEdge(start.height + deltaPx),
    onCommit: (start, deltaPx) => sizingApi.commitBottomEdge(start.height + deltaPx),
    autoScrollContainer: scrollContainer,
  });

  return (
    <div
      data-dragging={isDragging}
      onPointerDown={onPointerDown}
      className="absolute left-0 right-0 z-20 cursor-row-resize flex items-center justify-stretch"
      style={{
        top: `${totalHeight - HANDLE_HITBOX / 2}px`,
        height: `${HANDLE_HITBOX}px`,
      }}
    >
      <div
        className={cn(
          'h-px w-full transition-colors bg-transparent data-[dragging=true]:bg-bg-info',
        )}
        data-dragging={isDragging}
      />
    </div>
  );
};

const clampPair = (nextTop: number, total: number): number => {
  if (total < 2 * MIN_HEIGHT_PX) return total / 2;
  return Math.max(MIN_HEIGHT_PX, Math.min(total - MIN_HEIGHT_PX, nextTop));
};

// Clamps the left stripe so the individual stripe (not the pair share) honors MIN_PRIMARY_FRACTION.
const clampStripePair = (left: number, pairTotal: number): number => {
  if (pairTotal < 2 * MIN_PRIMARY_FRACTION) return pairTotal / 2;
  return Math.max(MIN_PRIMARY_FRACTION, Math.min(pairTotal - MIN_PRIMARY_FRACTION, left));
};
